// ConsultationFeedback/ConsultationFeedbackForm.tsx

import React, {useContext, useEffect, useState} from 'react';
import {TextField} from '@mui/material';
import Button from 'Core/components/elements/Button/Button';
import {Message} from 'Core/components/Message';
import {FCCC, FRCC} from 'WideLayout/Layouts';
import EmojiRatingPicker from "Core/components/EmojiRatingPicker";
import CircularProgress from "Core/components/elements/CircularProgress";
import {useForm} from 'react-hook-form';
import SkillsPicker from 'Psychologist/SkillsPicker';
import {AuthContext, AuthContextType} from "Auth/AuthContext";
import {useProfile} from "User/ProfileContext";

interface ConsultationFeedbackFormProps {
    onConfirm: (
        consultationRating?: number | null,
        connectionRating?: number | null,
        connectionFeedback?: string,
        consultationFeedback?: string,
        dontWantToRate?: boolean,
        skills?: number[] | null
    ) => void;
    onCancel: () => void;
    loading: boolean;
    defaultConnectionRating?: number | null;
    defaultConsultationRating?: number | null;
    defaultConnectionFeedback?: string;
    defaultConsultationFeedback?: string;
    defaultDontWantToRate?: boolean;
    // Теперь используем number[] для списка навыков
    defaultSkills?: number[];
    cls?: string;
}

const ConsultationFeedbackForm: React.FC<ConsultationFeedbackFormProps> = (
    {
        onConfirm,
        onCancel,
        loading,
        cls,
        defaultConnectionRating = null,
        defaultConsultationRating = null,
        defaultConsultationFeedback = '',
        defaultConnectionFeedback = '',
        defaultDontWantToRate = false,
        defaultSkills = []
    }) => {
    const [consultationRating, setConsultationRating] = useState<number | null>(defaultConsultationRating);
    const [connectionRating, setConnectionRating] = useState<number | null>(defaultConnectionRating);
    const [consultationFeedback, setConsultationFeedback] = useState(defaultConsultationFeedback);
    const [connectionFeedback, setConnectionFeedback] = useState(defaultConnectionFeedback);
    const [dontWantToRate, setDontWantToRate] = useState(defaultDontWantToRate);
    const [hasLeftReview, setHasLeftReview] = useState<boolean>(false);
    const {isAuthenticated} = useContext(AuthContext) as AuthContextType;
    const {selectedProfile} = useProfile();

    const {control, getValues, setValue} = useForm<{ skills: number[] }>({
        defaultValues: {skills: defaultSkills},
    });

    // Гарантируем, что если defaultSkills изменится, форма подхватит новые значения
    useEffect(() => {
        setValue('skills', defaultSkills);
    }, [defaultSkills, setValue]);

    useEffect(() => {
        const reviewStatus = localStorage.getItem('hasLeftYandexReview');
        if (reviewStatus === 'true') {
            setHasLeftReview(true);
        }
    }, []);

    const handleConfirmClick = () => {
        if (!dontWantToRate && consultationRating === null) {
            Message.error('Пожалуйста, выберите оценку или нажмите "Не хочу оценивать".');
            return;
        }
        const skills = getValues('skills');
        onConfirm(
            consultationRating,
            connectionRating,
            consultationFeedback,
            connectionFeedback,
            dontWantToRate,
            skills
        );
    };

    const handleNoRate = () => {
        setDontWantToRate(true);
        setConsultationRating(null);
        setConnectionRating(null);
        setConsultationFeedback('');
        setConnectionFeedback('');
        const skills = getValues('skills');
        console.log('%%%%%%%%%%%%%%%%');
        console.log('%%%%%%%%%%%%%%%%');
        console.log('%%%%%%%%%%%%%%%%');
        console.log(skills);
        onConfirm(
            consultationRating,
            connectionRating,
            consultationFeedback,
            connectionFeedback,
            true,
            skills
        );
    };

    const handleLeaveReview = () => {
        localStorage.setItem('hasLeftYandexReview', 'true');
        setHasLeftReview(true);
        window.open('https://yandex.com/maps/org/life_help/60295629508/reviews/', '_blank');
    };

    return (
        <FCCC g={1} px={2} cls={cls} pos={'relative'}>
            <EmojiRatingPicker value={consultationRating} onChange={setConsultationRating}/>
            <TextField
                className={'mt-2'}
                label="Напишите ваш отзыв о консультации здесь"
                value={consultationFeedback}
                onChange={(e) => setConsultationFeedback(e.target.value)}
                multiline
                fullWidth
            />
            <EmojiRatingPicker value={connectionRating} onChange={setConnectionRating}/>
            <TextField
                className={'mt-2'}
                label="Напишите ваш отзыв о соединении здесь"
                value={connectionFeedback}
                onChange={(e) => setConnectionFeedback(e.target.value)}
                multiline
                fullWidth
            />

            {selectedProfile === 'psychologist' && <SkillsPicker control={control}/>}

            <FRCC g={1} wrap>
                <Button
                    onClick={handleConfirmClick}
                    className={'fw-bold'}
                    variant={'contained'}
                    disabled={loading || (!dontWantToRate && consultationRating === null)}>
                    Подтвердить
                </Button>
                <Button onClick={onCancel} variant={'contained'} disabled={loading}>
                    Отмена
                </Button>
                {!dontWantToRate && (
                    <Button onClick={handleNoRate} variant="text" disabled={loading}>
                        Не хочу оценивать
                    </Button>
                )}
            </FRCC>

            {consultationRating === 5 && connectionRating === 5 && !hasLeftReview && (
                <FCCC g={1} className={'mt-3'}>
                    <p style={{textAlign: 'center'}}>
                        Ваш отзыв на Яндекс поможет другим клиентам найти своего психолога:
                    </p>
                    <Button onClick={handleLeaveReview} variant={'contained'} className={'fw-bold'}>
                        Оставить отзыв
                    </Button>
                </FCCC>
            )}

            {loading && (
                <FCCC
                    pos={'absolute'}
                    className={'backdrop-blur-10'}
                    top={0}
                    left={0}
                    zIndex={55}
                    rounded={3}
                    w={'100%'}
                    h={'100%'}>
                    <CircularProgress sx={{marginBottom: '20px'}} size={'3rem'}/>
                </FCCC>
            )}
        </FCCC>
    );
};

export default ConsultationFeedbackForm;
